import React, { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

export interface SqlCodeBlockProps {
  /**
   * SQL code to display
   */
  code: string;
  /**
   * ARIA label for the copy button
   */
  copyLabel?: string;
  /**
   * Role badge to display above the code (e.g., "ACCOUNTADMIN", "SECURITYADMIN")
   */
  runAsRole?: string;
}

/**
 * SqlCodeBlock Component
 *
 * Displays SQL code with syntax highlighting and a copy-to-clipboard button.
 * Includes accessibility features like aria-live announcements.
 *
 * @component
 * @example
 * ```tsx
 * <SqlCodeBlock
 *   code="CREATE WAREHOUSE MY_WH;"
 *   runAsRole="ACCOUNTADMIN"
 *   copyLabel="Copy warehouse creation SQL"
 * />
 * ```
 */
export const SqlCodeBlock: React.FC<SqlCodeBlockProps> = ({ code, copyLabel = 'Copy SQL', runAsRole }) => {
  const [copied, setCopied] = useState<boolean>(false);

  /**
   * Copies SQL code to clipboard and shows success feedback
   */
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy SQL:', err);
    }
  };

  return (
    <div className="space-y-2">
      {runAsRole !== undefined && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-dataops-primary-blue/10 px-2 py-1 text-xs text-dataops-primary-blue ring-1 ring-inset ring-dataops-primary-blue/20">
            Run as: {runAsRole}
          </span>
        </div>
      )}
      <div className="relative">
        <pre className="bg-gray-900 text-green-400 rounded-md p-4 overflow-x-auto text-sm font-mono">
          <code>{code}</code>
        </pre>
        <motion.button
          type="button"
          onClick={() => {
            void handleCopy();
          }}
          className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-md text-xs text-white transition-colors duration-150"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={copied ? 'SQL copied to clipboard' : copyLabel}
        >
          {copied ? (
            <motion.span
              className="flex items-center gap-1.5"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500 }}
            >
              <CheckIcon className="h-4 w-4 text-green-highlight" aria-hidden="true" />
              <span className="text-green-highlight">Copied!</span>
            </motion.span>
          ) : (
            <>
              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
              Copy SQL
            </>
          )}
        </motion.button>
        {/* Accessibility: Announce copy success */}
        {copied && (
          <span className="sr-only" role="status" aria-live="polite">
            SQL copied to clipboard
          </span>
        )}
      </div>
    </div>
  );
};
